import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  createCsExpansao,
  createCsInteraction,
  createCsNps,
  createCsTratativa,
  fetchCsAccount,
  generateCsPortalToken,
  updateCsAccount,
  updateCsExpansao,
  type CsAccountDetail,
  type CsCarteiraStage,
} from '@/services/csService'

export function useCsAccountDetailPage(accountId: string | undefined) {
  const [account, setAccount] = useState<CsAccountDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    try {
      setAccount(await fetchCsAccount(accountId))
    } catch {
      setAccount(null)
      toast.error('Conta não encontrada ou sem permissão.')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    void load()
  }, [load])

  const saveFields = async (payload: Record<string, unknown>) => {
    if (!accountId) return
    const updated = await updateCsAccount(accountId, payload)
    setAccount((prev) => (prev ? { ...prev, ...updated } : prev))
    toast.success('Conta atualizada.')
    await load()
  }

  const addInteraction = async (payload: { tipo: string; descricao?: string; resultado?: string }) => {
    if (!accountId) return
    await createCsInteraction(accountId, payload)
    toast.success('Interação registrada.')
    await load()
  }

  const addTratativa = async (payload: {
    tipo: string
    descricao: string
    responsavelUserId: string
    resultado?: string
  }) => {
    if (!accountId) return
    await createCsTratativa(accountId, payload)
    toast.success('Tratativa registrada.')
    await load()
  }

  const addNps = async (payload: { score: number; comentario?: string | null }) => {
    if (!accountId) return
    await createCsNps(accountId, payload)
    toast.success('NPS registrado.')
    await load()
  }

  const addExpansao = async (payload: {
    tipo: 'UPSELL' | 'CROSS_SELL'
    oportunidade: string
    potencialNota?: string | null
    status?: string
  }) => {
    if (!accountId) return
    await createCsExpansao(accountId, payload)
    toast.success('Oportunidade registrada.')
    await load()
  }

  const setExpansaoStatus = async (expansaoId: string, status: string) => {
    if (!accountId) return
    await updateCsExpansao(accountId, expansaoId, { status })
    toast.success('Status atualizado.')
    await load()
  }

  const copyPortalLink = async () => {
    if (!accountId) return
    const { token } = await generateCsPortalToken(accountId)
    const url = `${window.location.origin}/portal/${token}`
    await navigator.clipboard.writeText(url)
    toast.success('Link do portal copiado.')
  }

  const setStage = (csStage: CsCarteiraStage) => saveFields({ csStage })

  return {
    account,
    loading,
    reload: load,
    saveFields,
    addInteraction,
    addTratativa,
    addNps,
    addExpansao,
    setExpansaoStatus,
    copyPortalLink,
    setStage,
  }
}
